// Fan-side newsroom posting.
//
// The newsroom route is subscription-gated, so anyone viewing it is an active
// subscriber. Every subscriber gets a "post" panel that lets them ADD a post
// (text / photo / link → pending admin approval) and REMOVE (withdraw) their own
// still-pending submissions. Subscribers submit through /api/newsroom/submit-block
// (capped at 10 pending; approved posts earn $15 in wallet credit).
//
// Moderator-only powers (in-place pin/edit/delete of any post) stay gated behind
// useIsNewsroomModerator() in NewsroomPage — this panel is for everyone.
//
// Self-contained: no changes to the public newsroom feed or its data path.

import { useCallback, useEffect, useState } from 'react'
import { X, Type, Image as ImageIcon, Link as LinkIcon, Plus, AlertCircle, CheckCircle2, Clock, Trash2, Gift, Wallet } from 'lucide-react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

// Wallet credit (in dollars) a poster earns when an admin approves their post.
// Mirrors REWARD_AMOUNT in the backend newsroomModerationController.
const REWARD_DOLLARS = 15
// Max still-pending submissions a subscriber may queue (mirrors backend cap;
// the server enforces this and returns HTTP 429 past the limit).
const MAX_PENDING = 10

type BlockType = 'text' | 'photo' | 'link'

interface Submission {
  id: string
  block_type: string
  content: any
  s3_key: string | null
  file_url?: string | null
  approval_status: 'pending' | 'approved' | 'rejected'
  rejection_reason: string | null
  created_at: string
  is_pinned?: boolean
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('auth_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * Fetches the current user's wallet balance (in dollars) from GET /api/wallet/balance.
 * Returns null while loading OR on any failure, so callers can hide the credit
 * display gracefully — it must never break the newsroom page.
 */
function useWalletBalance(enabled: boolean): number | null {
  const [balance, setBalance] = useState<number | null>(null)
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`${API_URL}/api/wallet/balance`, { headers: authHeader() })
        if (!res.ok) return
        const j = await res.json()
        if (!cancelled && typeof j?.balance === 'number') setBalance(j.balance)
      } catch {
        /* degrade silently — leave balance null (hidden) */
      }
    })()
    return () => { cancelled = true }
  }, [enabled])
  return balance
}

/**
 * Probes whether the current fan is a newsroom moderator.
 * Returns null while loading, then true/false. Defaults to false on any error
 * so non-moderators never see moderator UI.
 */
export function useIsNewsroomModerator(): boolean | null {
  const [isModerator, setIsModerator] = useState<boolean | null>(null)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`${API_URL}/api/newsroom/moderator/status`, { headers: authHeader() })
        if (!res.ok) { if (!cancelled) setIsModerator(false); return }
        const j = await res.json()
        if (!cancelled) setIsModerator(!!j.is_moderator)
      } catch {
        if (!cancelled) setIsModerator(false)
      }
    })()
    return () => { cancelled = true }
  }, [])
  return isModerator
}

/**
 * Controlled "Edit newsroom" panel for moderators. Rendered by NewsroomPage,
 * opened from the header Edit button.
 */
export function NewsroomComposer({
  open,
  onClose,
  startCompose = false,
  initialType = 'text',
}: {
  open: boolean
  onClose: () => void
  startCompose?: boolean
  initialType?: BlockType
}) {
  const [showComposer, setShowComposer] = useState(false)
  const [items, setItems] = useState<Submission[]>([])
  const [loading, setLoading] = useState(false)
  const balance = useWalletBalance(open)

  const pendingCount = items.filter(s => s.approval_status === 'pending').length
  const atCap = pendingCount >= MAX_PENDING

  // When the panel is opened from the feed entry card, jump straight into the
  // compose form (Facebook-style). Opening from the header shows the list first.
  useEffect(() => {
    if (open) setShowComposer(startCompose && !atCap)
    else setShowComposer(false)
  }, [open, startCompose, atCap])

  const loadSubmissions = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/newsroom/moderator/my-submissions`, { headers: authHeader() })
      const j = res.ok ? await res.json() : { data: [] }
      setItems(j.data || [])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) loadSubmissions()
  }, [open, loadSubmissions])

  const withdraw = useCallback(async (id: string) => {
    // Optimistic removal
    const prev = items
    setItems(list => list.filter(s => s.id !== id))
    const res = await fetch(`${API_URL}/api/newsroom/moderator/submissions/${id}`, {
      method: 'DELETE',
      headers: authHeader(),
    })
    if (!res.ok) setItems(prev) // revert on failure
  }, [items])

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[88vh] overflow-y-auto">
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Your posts</h2>
            <p className="text-sm text-gray-500 mt-0.5">Share updates with the community — they go live once an admin approves.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 -mr-1 -mt-1 p-1" aria-label="Close">
            <X size={22} />
          </button>
        </div>

        <div className="px-6 py-5">
          {/* Incentive + credit — calm, tasteful */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-5">
            <div className="flex-1 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-pink-50 text-pink-700">
              <Gift size={17} className="flex-shrink-0" />
              <span className="text-sm font-medium">Earn ${REWARD_DOLLARS} in credit when a post is approved</span>
            </div>
            {balance !== null && (
              <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-gray-50 text-gray-700">
                <Wallet size={16} className="flex-shrink-0 text-gray-500" />
                <span className="text-sm">Credit <span className="font-semibold text-gray-900">${balance.toFixed(2)}</span></span>
              </div>
            )}
          </div>

          {/* Start a post */}
          {atCap ? (
            <div className="mb-6 px-4 py-3 rounded-xl bg-yellow-50 border border-yellow-200 text-sm text-yellow-800 flex items-start gap-2">
              <Clock size={16} className="mt-0.5 flex-shrink-0" />
              <span>You have {MAX_PENDING} posts awaiting review — please wait for approval before adding more.</span>
            </div>
          ) : (
            <button
              data-testid="start-post"
              onClick={() => setShowComposer(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-pink-500 hover:bg-pink-600 rounded-xl text-white font-semibold shadow-sm transition-colors mb-6"
            >
              <Plus size={18} />
              Create a post
            </button>
          )}

          {/* Your posts */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Submissions</h3>
            <span className="text-xs font-medium text-gray-500">{pendingCount} / {MAX_PENDING} pending</span>
          </div>
          {loading ? (
            <p className="text-sm text-gray-500 py-2">Loading…</p>
          ) : items.length === 0 ? (
            <div className="text-center py-8 px-4 rounded-xl bg-gray-50">
              <p className="text-sm font-medium text-gray-600">Nothing here yet</p>
              <p className="text-xs text-gray-500 mt-0.5">Tap “Create a post” to share your first update.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map(s => (
                <SubmissionRow key={s.id} sub={s} onRemove={s.approval_status === 'pending' ? () => withdraw(s.id) : undefined} />
              ))}
            </div>
          )}
        </div>
      </div>

      {showComposer && (
        <ComposerModal
          pendingCount={pendingCount}
          initialType={initialType}
          onClose={() => setShowComposer(false)}
          onSubmitted={() => { setShowComposer(false); loadSubmissions() }}
        />
      )}
    </div>
  )
}

function ComposerModal({ pendingCount, initialType = 'text', onClose, onSubmitted }: { pendingCount: number; initialType?: BlockType; onClose: () => void; onSubmitted: () => void }) {
  const [type, setType] = useState<BlockType>(initialType)
  const [text, setText] = useState('')
  const [caption, setCaption] = useState('')
  const [linkUrl, setLinkUrl] = useState('')
  const [linkTitle, setLinkTitle] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [atCap, setAtCap] = useState(pendingCount >= MAX_PENDING)

  const canSubmit = (
    (type === 'text' && text.trim().length > 0) ||
    (type === 'photo' && !!file) ||
    (type === 'link' && /^https?:\/\//i.test(linkUrl.trim()))
  ) && !submitting && !atCap

  const submit = useCallback(async () => {
    setError(null)
    setSubmitting(true)
    try {
      const fd = new FormData()
      fd.append('block_type', type)
      if (type === 'text') {
        fd.append('content', JSON.stringify({ text: text.trim() }))
      } else if (type === 'link') {
        fd.append('content', JSON.stringify({ url: linkUrl.trim(), title: linkTitle.trim() || null }))
      } else if (type === 'photo') {
        fd.append('content', JSON.stringify({ caption: caption.trim() || null }))
        if (file) fd.append('file', file)
      }

      // Every subscriber submits through the shared /submit-block route. Moderators
      // use it too (they keep their extra edit/pin/delete powers elsewhere).
      const res = await fetch(`${API_URL}/api/newsroom/submit-block`, {
        method: 'POST',
        headers: authHeader(),
        body: fd,
      })
      if (res.status === 429) {
        // At the pending cap — surface the server's message and lock submit.
        const j = await res.json().catch(() => ({}))
        setAtCap(true)
        throw new Error(j.error || `You have ${MAX_PENDING} posts awaiting review — please wait for approval.`)
      }
      if (!res.ok) {
        const j = await res.json().catch(() => ({}))
        throw new Error(j.error || 'Submission failed')
      }
      onSubmitted()
    } catch (e: any) {
      setError(e?.message || 'Submission failed')
    } finally {
      setSubmitting(false)
    }
  }, [type, text, caption, linkUrl, linkTitle, file, onSubmitted])

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[92vh] overflow-y-auto">
        {/* Header with author identity — Facebook composer feel */}
        <div className="flex items-center gap-3 px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center text-white font-semibold shadow-sm flex-shrink-0">
            C
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-gray-900 leading-tight">Create a post</h2>
            <p className="text-xs text-gray-500">Posting to the Caryn Community channel · reviewed before it goes live</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" aria-label="Close">
            <X size={22} />
          </button>
        </div>

        <div className="px-6 py-5">
          {/* Type selector — spacious segmented control */}
          <div className="grid grid-cols-3 gap-2 mb-4 p-1 bg-gray-100 rounded-xl">
            <TypePill icon={<Type size={17} />} label="Text" active={type === 'text'} onClick={() => setType('text')} />
            <TypePill icon={<ImageIcon size={17} />} label="Photo" active={type === 'photo'} onClick={() => setType('photo')} />
            <TypePill icon={<LinkIcon size={17} />} label="Link" active={type === 'link'} onClick={() => setType('link')} />
          </div>

          {type === 'text' && (
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="What's on your mind? Share it with the Caryn Community…"
              rows={6}
              autoFocus
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent focus:bg-white text-base placeholder:text-gray-400 resize-none transition-colors"
            />
          )}

          {type === 'photo' && (
            <div className="space-y-3">
              <label className="flex flex-col items-center justify-center gap-2 w-full py-8 px-4 bg-gray-50 border-2 border-dashed border-gray-200 hover:border-pink-300 hover:bg-pink-50/40 rounded-xl cursor-pointer transition-colors text-center">
                <ImageIcon size={26} className="text-pink-400" />
                <span className="text-sm font-medium text-gray-700">{file ? file.name : 'Choose a photo to share'}</span>
                <span className="text-xs text-gray-400">PNG or JPG</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => setFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </label>
              <input
                type="text"
                value={caption}
                onChange={e => setCaption(e.target.value)}
                placeholder="Add a caption (optional)"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent focus:bg-white text-base placeholder:text-gray-400 transition-colors"
              />
            </div>
          )}

          {type === 'link' && (
            <div className="space-y-3">
              <input
                type="url"
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                placeholder="https://…"
                autoFocus
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent focus:bg-white text-base placeholder:text-gray-400 transition-colors"
              />
              <input
                type="text"
                value={linkTitle}
                onChange={e => setLinkTitle(e.target.value)}
                placeholder="Add a title (optional)"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent focus:bg-white text-base placeholder:text-gray-400 transition-colors"
              />
            </div>
          )}

          {/* Subtle reward banner */}
          <div className="flex items-center gap-2 mt-4 px-3.5 py-2.5 rounded-xl bg-pink-50 text-pink-700">
            <Gift size={16} className="flex-shrink-0" />
            <span className="text-sm">Earn <span className="font-semibold">${REWARD_DOLLARS} in credit</span> when this post is approved.</span>
          </div>

          {error && (
            <div className="mt-3 px-3.5 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-start gap-2">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-center gap-3 mt-5">
            <span className="text-xs text-gray-400 mr-auto">{pendingCount} / {MAX_PENDING} pending</span>
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 font-medium rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={!canSubmit}
              className="px-7 py-2.5 bg-pink-500 hover:bg-pink-600 disabled:bg-gray-300 text-white font-semibold rounded-xl shadow-sm transition-colors"
            >
              {submitting ? 'Posting…' : atCap ? 'Limit reached' : 'Post'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function TypePill({ icon, label, active, onClick }: { icon: React.ReactNode; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
        active
          ? 'bg-white text-pink-600 shadow-sm'
          : 'bg-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {icon}
      {label}
    </button>
  )
}

function SubmissionRow({ sub, onRemove }: { sub: Submission; onRemove?: () => void }) {
  const statusMeta = {
    pending:  { icon: <Clock size={14} />, color: 'bg-yellow-50 text-yellow-700 border-yellow-200', label: 'Pending review' },
    approved: { icon: <CheckCircle2 size={14} />, color: 'bg-green-50 text-green-700 border-green-200', label: 'Approved' },
    rejected: { icon: <X size={14} />, color: 'bg-red-50 text-red-700 border-red-200', label: 'Rejected' },
  }[sub.approval_status]

  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium border ${statusMeta.color}`}>
          {statusMeta.icon}
          {statusMeta.label}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{new Date(sub.created_at).toLocaleString()}</span>
          {onRemove && (
            <button
              onClick={onRemove}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 transition-colors"
              aria-label="Remove submission"
            >
              <Trash2 size={13} />
              Remove
            </button>
          )}
        </div>
      </div>

      {sub.block_type === 'text' && sub.content?.text && (
        <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{sub.content.text}</p>
      )}
      {sub.block_type === 'link' && sub.content?.url && (
        <a href={sub.content.url} target="_blank" rel="noreferrer" className="text-sm text-pink-600 hover:underline break-all">
          {sub.content.title || sub.content.url}
        </a>
      )}
      {sub.block_type === 'photo' && sub.file_url && (
        <img src={sub.file_url} alt="" className="mt-1 max-h-40 rounded border border-gray-200 object-cover" />
      )}

      {sub.approval_status === 'rejected' && sub.rejection_reason && (
        <p className="mt-2 text-xs text-red-600 italic">"{sub.rejection_reason}"</p>
      )}
    </div>
  )
}
